import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import InputForm from "../../../components/input/input-form.jsx";
import Button from "../../../components/button/button.jsx";
import {FaEraser, FaSearch, FaSignOutAlt, FaTrash} from "react-icons/fa";
import {useQuery} from "@tanstack/react-query";
import sessionsService from "../../../services/web/sessions-service.js";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../utils/aggrid-utils.js";
import projectUtils from "../../../utils/project-utils.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {useLoaderData} from "react-router";
import useLogout from "../../../hooks/use-logout.js";

const Sessions = () => {
    const loaderData = useLoaderData();
    const logout = useLogout();

    const [data, setData] = useState([])
    const [filter, setFilter] = useState("");

    const {data: dataFetch, isSuccess, refetch, isLoading} = useQuery({
        queryFn: () => sessionsService.list(),
        queryKey: ["sessions"]
    });

    useEffect(() => {
        if (!isSuccess) return;

        setData(dataFetch);
    }, [dataFetch, isSuccess]);

    useEffect(() => {
        if (loaderData?.adminLimitBypass) {
            projectUtils.swalMessage(
                "warning",
                "Atenção",
                "Limite de acessos simultâneos atingido. Para acesso total ao sistema é necessário encerrar a sessão de algum usuário.",
                "Ok",
            );
        }
    }, [loaderData]);

    const [columnDefs,] = useState([
        { field: "login", headerName: "Login", sort: "asc", flex: 1},
        { field: "ip", headerName: "IP", flex: 1},
        { field: "dtLogged", headerName: "D/H Login", flex: 1},
        { field: "dtExpiration", headerName: "D/H Expiração", flex: 1},
        { field: "application", headerName: "Módulo", flex: 1},
        {
            field: "",
            headerName: "Ação",
            width: 60,
            cellRenderer: ({data}) => {
                const destroySession = () => {
                    if (data.isLoggedUser) {
                        logout();
                        return
                    }

                    const session = { id: data.id, userId: data.userId, login: data.login }

                    sessionsService.destroySession(session)
                        .then(() => {
                            projectUtils.success(data.isLoggedUser, "Sessão encerrada com sucesso");
                            refetch()
                        })
                        .catch((error) => {
                            console.error(error);
                            let message = 'Não foi possível encerrar.';
                            if (error.response.status !== 500) {
                                message = error.response.data.message;
                            }
                            projectUtils.swalErrorMessage('', message, 'Ok');
                        })
                }

                return (
                    <Button target={"danger"} onClick={destroySession}>
                        <FaTrash />
                    </Button>
                )
            }
        },
    ]);

    const gridRef = useRef(undefined);

    const defaultColDef = useMemo(() => ({resizable: false}), []);

    const apply = useCallback(() => {
        setData(dataFetch.filter(d => d.login?.toLowerCase().includes(filter?.toLowerCase())))
    }, [filter, dataFetch])

    const clear = useCallback(() => {
        setData(dataFetch);
        setFilter("");
    }, [dataFetch])

    useGlobalKey("Enter", () => {
        if (!isLoading) apply()
    });
    useGlobalKey("Escape", () => {
        if (!isLoading) clear()
    });

    return (
        <Template>
            <Template.Header>
                <Template.Title className={loaderData?.adminLimitBypass ? "ps-0!" : ""}>Usuários logados</Template.Title>

                {
                    loaderData?.adminLimitBypass &&
                    <Button onClick={logout}>
                        <FaSignOutAlt /> Sair
                    </Button>
                }
            </Template.Header>

            <Template.Body className={"space-y-2"}>
                <Card>
                    <Card.Body className="flex justify-between gap-4">
                        <InputForm
                            classNameWrapper={"w-full"}
                            id={"name"}
                            label={"Login"}
                            value={filter}
                            onChange={({target: {value}}) => setFilter(value)}
                        />

                        <div className={"flex gap-2"}>
                            <Button target={"default"} onClick={clear} title={"Limpar"}>
                                <FaEraser />
                            </Button>
                            <Button onClick={apply} title={"Buscar"}>
                                <FaSearch />
                            </Button>
                        </div>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body>
                        <div style={{height: "calc(100vh - 153px)"}}>
                            <AgGridReact
                                ref={gridRef}
                                rowHeight={35}
                                rowData={data}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                pagination
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default Sessions;